import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Loader2, Send, Inbox, Bug, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  open: 'bg-sky-500/15 text-sky-500 border-sky-500/30',
  in_progress: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  closed: 'bg-muted text-muted-foreground border-border/40',
};

function Timeline({ events }) {
  const shown = (events || []).filter((e) => ['comment', 'question', 'status'].includes(e.type));
  if (!shown.length) return null;
  return (
    <div className="mt-3 space-y-2 border-l-2 border-border/40 pl-3">
      {shown.map((e) => (
        <div key={e.event_id} className="text-xs" data-testid={`mf-event-${e.event_id}`}>
          {e.type === 'status' ? (
            <span className="text-muted-foreground">Status changed to <b className="text-foreground">{String(e.to).replace('_', ' ')}</b></span>
          ) : (
            <div>
              <span className={`font-medium ${e.from_reporter ? 'text-primary' : 'text-foreground'}`}>
                {e.from_reporter ? 'You' : e.author_name}
                {e.type === 'question' && !e.from_reporter ? ' asked' : ''}:
              </span>{' '}
              <span className="text-muted-foreground">{e.text}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function MyFeedbackPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listMyFeedback();
      setItems(r.items || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load your feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Opening this page clears the sidebar's unread indicator: stamp the
  // last-seen time and tell the sidebar to reset its count immediately.
  useEffect(() => {
    api.setUiPref('my_feedback_last_seen', new Date().toISOString()).catch(() => {});
    window.dispatchEvent(new Event('my-feedback-seen'));
  }, []);

  // Deep link from an update notification/email: /my-feedback?id=<feedback_id>
  const [params] = useSearchParams();
  const focusId = params.get('id');
  useEffect(() => {
    if (loading || !focusId) return;
    const el = document.querySelector(`[data-testid="mf-item-${focusId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-primary');
      const t = setTimeout(() => el.classList.remove('ring-2', 'ring-primary'), 2600);
      return () => clearTimeout(t);
    }
  }, [loading, focusId]);

  const reply = async (id) => {
    const text = (drafts[id] || '').trim();
    if (!text) return;
    setSending((s) => ({ ...s, [id]: true }));
    try {
      await api.addFeedbackComment(id, text);
      setDrafts((d) => ({ ...d, [id]: '' }));
      toast.success('Reply sent');
      load();
    } catch (e) {
      toast.error(e.message || 'Could not send reply');
    } finally {
      setSending((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="my-feedback-page">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-primary" /> My Feedback
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Track your reports and reply to the team's questions.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="mf-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
          You haven't submitted any feedback yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="mf-list">
          {items.map((f) => (
            <div key={f.feedback_id} className="bg-card/60 border border-border/40 rounded-2xl p-5" data-testid={`mf-item-${f.feedback_id}`}>
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {f.type === 'bug' ? <Bug className="w-4 h-4 text-rose-400 shrink-0" /> : <Lightbulb className="w-4 h-4 text-amber-400 shrink-0" />}
                  <span className="font-medium text-foreground truncate">{f.title || f.description?.slice(0, 80) || 'Feedback'}</span>
                </div>
                <Badge className={`text-[10px] shrink-0 ${STATUS_STYLE[f.status] || STATUS_STYLE.open}`}>
                  {String(f.status || 'open').replace('_', ' ')}
                </Badge>
              </div>
              {f.title && f.description ? <p className="text-xs text-muted-foreground">{f.description}</p> : null}
              <Timeline events={f.events} />
              <div className="mt-3 flex items-center gap-2">
                <input
                  value={drafts[f.feedback_id] || ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [f.feedback_id]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && reply(f.feedback_id)}
                  placeholder="Reply to the team…"
                  className="flex-1 h-9 rounded-lg bg-background border border-border/50 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40"
                  data-testid={`mf-reply-input-${f.feedback_id}`}
                />
                <Button size="sm" onClick={() => reply(f.feedback_id)} disabled={sending[f.feedback_id]} data-testid={`mf-reply-send-${f.feedback_id}`}>
                  {sending[f.feedback_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
